import { useCallback, useEffect, useRef, useState } from 'react';

import { getAuth } from 'firebase/auth';

import { makeAuthenticatedApiCall } from '../apiHelper';

// Cache localStorage pour que le tracker survive aux remounts/refresh navigateur.
// NAMESPACÉ PAR UTILISATEUR (uid Firebase). Une clé globale faisait FUITER les
// dossiers d'une agence vers la session suivante : à la reconnexion avec
// d'AUTRES identifiants, l'ancien cache était réhydraté et affichait le toast
// « 1 terminé » d'un autre compte. La clé par-uid rend toute lecture cross-user
// impossible ; sans uid connu (auth non résolue) on ne lit/écrit RIEN.
const STORAGE_PREFIX = 'skipcar.cg.in_flight_uploads.v1';
export const storageKeyFor = (uid: string | null): string | null => (uid ? `${STORAGE_PREFIX}.${uid}` : null);

export const loadFromStorage = (uid: string | null): InFlightUploadRow[] => {
  const key = storageKeyFor(uid);
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveToStorage = (uid: string | null, rows: InFlightUploadRow[]): void => {
  const key = storageKeyFor(uid);
  if (!key) return; // jamais persister sans utilisateur identifié
  try {
    window.localStorage.setItem(key, JSON.stringify(rows));
  } catch {
    // Ignorer silencieusement (quota dépassé, mode privé, etc.) —
    // l'état en mémoire reste valide pour la session courante.
  }
};

/**
 * Source de vérité côté agence pour les uploads CG en arrière-plan.
 *
 * Combine :
 *  - un GET initial sur /api/cg/in-flight au mount (et toutes les 30 s en
 *    fallback si la WS est coupée)
 *  - un WebSocket vers le backend qui push les transitions d'état des
 *    in-flight uploads (cross-tab sync inclus)
 *
 * L'URL WS est dérivée de l'URL de l'API : protocole ws/wss selon http/https,
 * token Firebase passé en query string `?token=…` (cf. server.ts auth handler).
 *
 * À l'usage : monter une seule fois (typiquement dans le provider de
 * background uploads ou directement dans AgencyCGList). Les listeners
 * sont déduplique via une ref globale.
 */

export interface InFlightUploadRow {
  id: string;
  agency_org_id: string;
  agency_user_id: number;
  state: 'queued' | 'uploading' | 'analyzing' | 'submitting' | 'done' | 'cancelled' | 'error';
  files_total: number;
  files_uploaded: number;
  files_analyzed: number;
  registration_id: number | null;
  deal_id: number | null;
  payload: unknown;
  error_message: string | null;
  started_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface UseInFlightUploadsResult {
  uploads: InFlightUploadRow[];
  isLoading: boolean;
  refresh: () => Promise<void>;
  /**
   * Injection directe d'une row dans l'état local — utilisée par
   * BackgroundUploadsProvider.startUpload pour éviter d'attendre le
   * poll ou le push WS (utile aux users god dont la WS auth peut
   * échouer faute d'idPipedrive en Firestore).
   */
  upsertLocal: (row: InFlightUploadRow) => void;
  /** Retire immédiatement une row du state local + localStorage. */
  removeLocal: (id: string) => void;
  /** true si la WS est OPEN — exposé pour afficher un badge "Hors-ligne". */
  wsConnected: boolean;
}

const buildWsUrl = async (): Promise<string | null> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken();
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // En dev local, le backend tourne sur localhost:<VITE_BACKEND_PORT> (3002
  // dans le .env actuel) directement → pas de path. En prod (derrière nginx),
  // il faut taper le path `/ws` car c'est le seul `location` configuré avec
  // `proxy_set_header Upgrade`. Cf. AgencyDealsList.tsx:216 qui suit le même
  // pattern VITE_BACKEND_PORT || '3001'.
  if (import.meta.env.DEV && window.location.hostname === 'localhost') {
    const backendPort = import.meta.env.VITE_BACKEND_PORT || '3001';
    return `${proto}://${window.location.hostname}:${backendPort}/?token=${encodeURIComponent(token)}`;
  }
  return `${proto}://${window.location.host}/ws?token=${encodeURIComponent(token)}`;
};

export const useInFlightUploads = (): UseInFlightUploadsResult => {
  // uid de l'utilisateur dont le cache/état est actuellement chargé. Sert à
  // (a) namespacer la persistance localStorage et (b) détecter une bascule de
  // compte pour purger + refetch (cf. effet auth plus bas).
  const storageUidRef = useRef<string | null>(getAuth().currentUser?.uid ?? null);

  // Hydrate depuis le cache DE CET uid : le tracker reste visible immédiatement
  // au refresh navigateur, sans jamais exposer le cache d'un autre compte
  // (uid inconnu → []).
  const [uploads, setUploadsRaw] = useState<InFlightUploadRow[]>(() => loadFromStorage(storageUidRef.current));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [wsConnected, setWsConnected] = useState<boolean>(false);

  // Wrapper qui persiste à chaque set — garantit que tout chemin de mise à
  // jour (refresh, WS, upsertLocal) reste mirroré sur disque, sous la clé de
  // l'utilisateur courant.
  const setUploads: typeof setUploadsRaw = useCallback((value) => {
    setUploadsRaw((prev) => {
      const next = typeof value === 'function' ? (value as (p: InFlightUploadRow[]) => InFlightUploadRow[])(prev) : value;
      saveToStorage(storageUidRef.current, next);
      return next;
    });
  }, []);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef<boolean>(true);

  const refresh = useCallback(async () => {
    // Garde : ne tente pas d'appeler l'API si Firebase auth pas encore prêt.
    // Sinon `makeAuthenticatedApiCall` lance "User not authenticated" et on
    // pollue la console. Le `onAuthStateChanged` ci-dessous re-déclenchera
    // refresh dès que l'auth résout.
    const auth = getAuth();
    if (!auth.currentUser) {
      if (mountedRef.current) setIsLoading(false);
      return;
    }
    try {
      const resp = await makeAuthenticatedApiCall<{ success: boolean; data: InFlightUploadRow[] }>(
        'get',
        '/api/cg/in-flight',
      );
      const remote = resp.data?.data ?? [];
      if (!mountedRef.current) return;
      // REPLACE le state local par le remote — le backend (PROGRESSING_PREDICATE
      // dans listByAgency) est autoritaire : si une row n'est plus dans la
      // réponse, c'est qu'elle a été abandonnée/terminée et NE DOIT PLUS
      // s'afficher. Le précédent MERGE conservait les vieilles rows en cache
      // indéfiniment (cf. bug "Dossier #167d4bbb 9/9 toujours visible").
      //
      // Le fallback offline reste assuré par le `catch` ci-dessous : si le
      // fetch échoue (réseau, auth expirée), le state local n'est PAS touché.
      setUploads(remote);
    } catch (error) {
      console.error('useInFlightUploads / refresh failed:', error);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [setUploads]);

  // Backoff exponentiel pour les reconnexions WS : 1s → 2s → 4s → 8s → 16s
  // → 30s (cap). Évite la tempête côté backend si le serveur tombe pendant
  // plusieurs minutes. Jitter 25% pour désynchroniser les clients massivement
  // reconnectés en même temps.
  const reconnectAttemptsRef = useRef<number>(0);
  const computeBackoff = useCallback((): number => {
    const attempt = reconnectAttemptsRef.current;
    const base = Math.min(30_000, 1000 * 2 ** attempt);
    const jitter = base * 0.25 * Math.random();
    return Math.round(base + jitter);
  }, []);

  // Ref qui pointe toujours sur la dernière instance de connectWs — évite
  // l'erreur "used before its declaration" causée par la dépendance circulaire
  // connectWs ↔ scheduleReconnect, et garantit qu'au déclenchement du timer
  // on appelle la version la plus récente du callback.
  const connectWsRef = useRef<() => void>(() => {});
  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    const delay = computeBackoff();
    reconnectAttemptsRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => connectWsRef.current(), delay);
  }, [computeBackoff]);

  const connectWs = useCallback(async () => {
    if (!mountedRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    try {
      const url = await buildWsUrl();
      if (!url) {
        // Firebase auth pas encore résolu (cas typique au 1er mount) →
        // retry après backoff au lieu de rester silencieusement hors-ligne.
        scheduleReconnect();
        return;
      }
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // Reset du compteur backoff : on est connecté, futurs reconnects
        // repartent à 1s.
        reconnectAttemptsRef.current = 0;
        setWsConnected(true);
        // Resync au reconnect : la liste locale peut diverger du backend
        // pendant une coupure (rows créées/modifiées via un autre tab).
        // Le merge protège les rows locales connues du backend.
        void refresh();
        // Heartbeat 30s pour garder la connexion ouverte (proxies / load balancers).
        if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 30_000);
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.type === 'cg-in-flight-update' && parsed.payload?.id) {
            const row = parsed.payload as InFlightUploadRow;
            setUploads((prev) => {
              const idx = prev.findIndex((u) => u.id === row.id);
              if (idx === -1) {
                // Nouveau row : seul `cancelled` n'est jamais ajouté ;
                // `done` reste visible avec son badge "Terminé" tant que
                // l'utilisateur n'a pas fermé manuellement le tracker.
                if (row.state === 'cancelled') return prev;
                return [...prev, row];
              }
              if (row.state === 'cancelled') return prev.filter((u) => u.id !== row.id);
              // Update inline — on conserve TOUS les autres états (uploading,
              // analyzing, submitting, done, error). C'est l'utilisateur qui
              // ferme le tracker, pas le backend.
              const next = [...prev];
              next[idx] = row;
              return next;
            });
          }
        } catch (error) {
          console.error('useInFlightUploads / parse WS message error:', error);
        }
      };

      ws.onclose = () => {
        if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
        wsRef.current = null;
        setWsConnected(false);
        scheduleReconnect();
      };

      ws.onerror = (error) => {
        console.error('useInFlightUploads / WS error:', error);
      };
    } catch (error) {
      console.error('useInFlightUploads / connectWs error:', error);
      scheduleReconnect();
    }
  }, [computeBackoff, refresh, scheduleReconnect, setUploads]);

  // Met à jour la ref à chaque re-render pour que scheduleReconnect appelle
  // toujours la version courante de connectWs.
  useEffect(() => {
    connectWsRef.current = connectWs;
  }, [connectWs]);

  useEffect(() => {
    mountedRef.current = true;
    // Hook Firebase auth : refresh + connectWs déclenchés une FOIS que
    // l'auth est résolue. Sinon le premier appel `void refresh()` plante
    // avec "User not authenticated" et le cache localStorage stale reste
    // affiché à vie sans jamais être purgé par un fetch réussi.
    const auth = getAuth();
    // Réactif au CHANGEMENT D'IDENTITÉ. Au login, logout, ou bascule de compte
    // (déconnexion + reconnexion avec d'autres identifiants), on purge l'état du
    // compte précédent — sinon le toast « 1 terminé » d'une autre agence reste
    // affiché — puis on refetch + reconnecte la WS scopés au nouvel utilisateur.
    // (Le garde `initialBootDone` précédent bootait UNE fois et ignorait tout
    // changement de compte → fuite cross-user.)
    // Sentinelle `undefined` distincte de tout uid réel ET de null : garantit
    // que le tout premier passage (utilisateur déjà connecté au mount) boote.
    let bootedUid: string | null | undefined = undefined;
    const handleAuthUser = (user: { uid: string } | null) => {
      if (!mountedRef.current) return;
      const uid = user?.uid ?? null;
      if (uid === bootedUid) return; // identité inchangée → rien à refaire
      bootedUid = uid;
      storageUidRef.current = uid;

      // La WS précédente était authentifiée pour l'ancien utilisateur : fermer.
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);

      if (!uid) {
        // Déconnexion : aucune donnée d'un compte ne doit rester en mémoire.
        // Le cache localStorage est namespacé par uid → il reste isolé et sera
        // rechargé tel quel si CE compte se reconnecte.
        setUploadsRaw([]);
        setIsLoading(false);
        return;
      }

      // Login / bascule : on repart du cache DE CET uid (vide au premier login
      // de ce compte), puis refetch + reconnexion WS scopés au nouvel
      // utilisateur (le backend liste par agence du compte authentifié).
      setUploadsRaw(loadFromStorage(uid));
      setIsLoading(true);
      void refresh();
      void connectWs();
    };
    // Boote pour l'utilisateur courant (hot-reload, navigation interne), puis
    // suit chaque transition d'auth.
    handleAuthUser(auth.currentUser);
    const unsubscribe = auth.onAuthStateChanged(handleAuthUser);

    // Fallback polling toutes les 30 s au cas où la WS est down.
    // Le `refresh()` interne re-checke `auth.currentUser` donc safe avant boot.
    const pollTimer = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        void refresh();
      }
    }, 30_000);
    return () => {
      mountedRef.current = false;
      unsubscribe();
      clearInterval(pollTimer);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [refresh, connectWs]);

  const upsertLocal = useCallback(
    (row: InFlightUploadRow) => {
      setUploads((prev) => {
        const idx = prev.findIndex((u) => u.id === row.id);
        if (idx === -1) {
          // Seul `cancelled` n'est jamais inséré. Les autres états restent
          // visibles jusqu'à dismiss manuel par l'utilisateur.
          if (row.state === 'cancelled') return prev;
          return [...prev, row];
        }
        if (row.state === 'cancelled') return prev.filter((u) => u.id !== row.id);
        const next = [...prev];
        next[idx] = row;
        return next;
      });
    },
    [setUploads],
  );

  const removeLocal = useCallback(
    (id: string) => {
      setUploads((prev) => prev.filter((u) => u.id !== id));
    },
    [setUploads],
  );

  return { uploads, isLoading, refresh, upsertLocal, removeLocal, wsConnected };
};
